import { Injectable, inject } from "@angular/core";

import { Action, createSelector, Selector, State, StateContext, Store } from "@ngxs/store";
import { tap } from "rxjs";

import { ICategory } from "../../shared/interface/category.interface";
import {
  GetCategoriesAction,
  GetLayerdCategoriesWithProductsAction,
  GetProductsWithCategoriesSlugAction,
  GetDetailedCategoryBySlugAction,
} from "../action/category.action";
import { CategoryService } from "../services/category.service";
import { IApiResponse } from "../interface/api-response.interface";
import { ThemeOptionService } from "../services/theme-option.service";
import { _IProduct } from "../interface/product.interface";
import { ProductStateModel } from "./product.state";
import { SILENT_ERROR } from "src/app/core/interceptors/silent-error.context";
import { HttpContext } from "@angular/common/http";



export class CategoryStateModel {
  category = {
    data: [] as ICategory[],
    total: 0,
  };
  categoryWithProducts: any[] | [];
  // products of each category, by slug: switching back to a category reuses its list
  categoryProductsBySlug: { [slug: string]: _IProduct[] };
  detailedCategory: ICategory | null;
}

@State<CategoryStateModel>({
  name: "category",
  defaults: {
    category: {
      data: [],
      total: 0,
    },
    categoryWithProducts: [],
    categoryProductsBySlug: {},
    detailedCategory: null,
  },
})
@Injectable()
export class CategoryState {
  private themeOptionService = inject(ThemeOptionService);
  private categoryService = inject(CategoryService);
  @Selector()
  static category(state: CategoryStateModel): CategoryStateModel["category"] {
    return state.category;
  }

  @Selector()
  static categoryWithProducts(state: CategoryStateModel) {
    return state.categoryWithProducts;
  }

  @Selector()
  static categoryDetailed(state: CategoryStateModel): ICategory | null {
    return state.detailedCategory;
  }

  /** Direct children of the currently opened (detailed) category */
  @Selector()
  static subCategories(state: CategoryStateModel): ICategory[] {
    const parentId = state.detailedCategory?.id;
    if (!parentId) return [];

    return (state.category?.data || []).filter(
      (c: ICategory) => c.parentCategoryId === parentId
    );
  }

  /** Products of one category (empty until GetProductsWithCategoriesSlugAction has loaded it) */
  static categoryProductsBySlug(slug: string) {
    return createSelector(
      [CategoryState],
      (state: CategoryStateModel): _IProduct[] => state.categoryProductsBySlug[slug] ?? []
    );
  }

  // @Action(GetCategoriesAction)
  // getCategories(
  //   ctx: StateContext<CategoryStateModel>,
  //   action: GetCategoriesAction,
  // ) {
  //   return this.categoryService.getCategories(action.payload).pipe(
  //     tap({
  //       next: (result) => {
  //         ctx.patchState({
  //           category: {
  //             data: result.data,
  //             total: result?.total ? result?.total : result?.data?.length,
  //           },
  //         });
  //       },
  //       error: (err) => {
  //         throw new Error(err?.error?.message);
  //       },
  //     }),
  //   );
  // }

  @Action(GetDetailedCategoryBySlugAction)
  getDetailedCategoryBySlug(ctx: StateContext<CategoryStateModel>, action: GetDetailedCategoryBySlugAction) {
    // Runs on the server too (SSR); the response is reused in the browser via TransferState
    this.themeOptionService.preloader = true;
    return this.categoryService.getCategoryDetailsBySlug(action.slug).pipe(
      tap({
        next: (results) => {
          // const result = results.data.find((product) => product.slug == slug);
          if (results) {
            const state = ctx.getState();
            ctx.patchState({
              ...state,
              detailedCategory: results.data,
            });
          } else {
            // void this.router.navigate(["/404"]);
          }
        },
        complete: () => {
          this.themeOptionService.preloader = false;
        },
        error: (err) => {
          throw new Error(err?.error?.message);
        },
      })
    );
  }

  @Action(GetCategoriesAction)
  getCategories(ctx: StateContext<CategoryStateModel>) {
    return this.categoryService.getAll().pipe(
      tap({
        next: (result: IApiResponse) => {
          ctx.patchState({
            category: {
              data: result.data,
              total: result.data.length,
            },
          });
        },
        error: (err) => {
          console.error(err);
        },
      })
    );
  }

  @Action(GetLayerdCategoriesWithProductsAction)
  getLayerdCategoriesWithProducts(
    ctx: StateContext<CategoryStateModel>,
    action: GetLayerdCategoriesWithProductsAction
  ) {
    this.themeOptionService.preloader = true;
    return this.categoryService.LayerdCategoriesWithProducts().pipe(
      tap({
        next: (result) => {
          ctx.patchState({
            categoryWithProducts: result.data,
          });
        },
        complete: () => {
          this.themeOptionService.preloader = false;
        },
        error: (err) => {
          throw new Error(err?.error?.message);
        },
      })
    );
  }

  @Action(GetProductsWithCategoriesSlugAction)
  getProductsWithCategoriesSlug(
    ctx: StateContext<CategoryStateModel>,
    action: GetProductsWithCategoriesSlugAction
  ) {
    this.themeOptionService.preloader = true;
    // silent: a failed product list shouldn't pop an error toast over the category page
    return this.categoryService
      .ProductsWithCategoriesSlug(action.slug, new HttpContext().set(SILENT_ERROR, true))
      .pipe(
        tap({
          next: (result) => {
            const state = ctx.getState();
            ctx.patchState({
              categoryProductsBySlug: {
                ...state.categoryProductsBySlug,
                [action.slug]: result.data,
              },
            });
          },
          complete: () => {
            this.themeOptionService.preloader = false;
          },
          error: (err) => {
            throw new Error(err?.error?.message);
          },
        })
      );
  }
}
